import { useGetUserOrderRatingsQuery } from 'api/orderRatings'
import { OrderRating } from 'api/orderRatings.types'
import { FeedbackTableRow } from 'components/FeedbackTable'
import { useCallback, useEffect, useState } from 'react'
import {
  getOrderRatingViewPath,
  RATING_DETAIL_PATH,
} from 'utilities/orderRatingViewPath'

// FR-30: "most recent 10 items by default, with a Load more control to fetch
// older history on demand" — matches the backend's own default (GET
// /users/:identifier/order_ratings defaults per_page to 10, max 50).
export const PAGE_SIZE = 10

// Re-exported for existing callers/tests of this module — the actual route
// base and getViewPath logic now live in utilities/orderRatingViewPath (see
// AI-1290's comment there for why this was promoted out of this scene).
export { RATING_DETAIL_PATH }

// One OrderRating wire row -> the shared FeedbackTable's row shape. Mirrors
// useOrderRatings.toFeedbackTableRow (see the RATING_DETAIL_PATH comment above
// for why this is a local copy, not a shared import).
export const toFeedbackTableRow = (rating: OrderRating): FeedbackTableRow => ({
  id: rating.id,
  rating: rating.rating,
  date: rating.date,
  customerName: rating.customerName,
  locationName: rating.storeName,
  resolvedBy: rating.resolvedBy,
  conversationId: rating.conversationId,
  comment: rating.comment,
  userId: rating.userId,
  userUid: rating.userUid,
})

// Re-exported under this scene's existing name — see utilities/orderRatingViewPath
// for the logic and doc comment (every row is viewable: a conversation-backed
// rating opens the RecoveryAI case detail; every other rating opens the
// Reports rating detail page, both keyed on the OrderRating hashid).
export const getViewPath = getOrderRatingViewPath

// Owns the Order feedback tab's query + "Load more" pagination. Unlike
// useOrderRatings (page-numbered, one page visible at a time), this surface
// ACCUMULATES pages into one growing list — FR-30 asks for 10 rows with a
// button to fetch older history on demand, not a page-by-page table.
//
// AI-1291 (FR-61): `skip`, default false, lets the caller withhold the fetch
// entirely when the operator lacks feedback-view permission — the endpoint
// is gated server-side too (N4), so an unskipped call here would just 403.
export const useOrderFeedback = (identifier: string, skip = false) => {
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<FeedbackTableRow[]>([])

  // `currentData`, NOT `data`. RTK Query defines `data` as the latest result
  // for ANY arg this hook has held, so between "Load more" bumping `page` and
  // the new page's response landing, `data` still holds the PREVIOUS page's
  // rows. The append effect below keys off the page the rows actually belong
  // to, and with `data` that window made it append page N-1 a second time —
  // one click produced 30 rows, with rows 11-20 duplicating 1-10 (verified on
  // sandbox: identical rating hashids). `currentData` is scoped to the current
  // arg and is undefined while the next page is in flight, so the effect
  // simply doesn't run until the right rows are there.
  const {
    currentData,
    isFetching,
    isError: queryIsError,
    refetch,
  } = useGetUserOrderRatingsQuery(
    {
      identifier,
      page,
      per_page: PAGE_SIZE,
    },
    { skip }
  )

  // A different customer's profile mounting this same hook instance (the
  // scene keys nothing on identifier today, but a future nav-without-remount
  // must not do this) resets back to page 1 with an empty accumulator —
  // mirrors the repo convention for an id-prop-driven reset.
  useEffect(() => {
    setPage(1)
    setRows([])
  }, [identifier])

  // Append on success. A failed page (isError) leaves `currentData` undefined
  // for that page's cache entry, so this simply doesn't run — the previously
  // loaded rows stay on screen while the error/retry affordance below reflects
  // the failure, rather than the accumulator silently truncating what already
  // loaded.
  //
  // Keyed on the response's OWN `meta.currentPage`, never the `page` state:
  // the two disagree exactly while a "Load more" fetch is in flight, and
  // trusting the state there is what produced the duplicate-page bug described
  // on the query above.
  useEffect(() => {
    if (!currentData) return
    const newRows = currentData.data.map(toFeedbackTableRow)
    const loadedPage = currentData.meta.currentPage
    setRows(previous =>
      loadedPage === 1 ? newRows : [...previous, ...newRows]
    )
  }, [currentData])

  const hasMore =
    !!currentData && currentData.meta.currentPage < currentData.meta.totalPages

  // Guarded the same way DetailsPane/RecoveryAI Activity's mutation buttons
  // are: don't let a second click fire a second in-flight fetch for the next
  // page while one is already running.
  const onLoadMore = useCallback(() => {
    if (isFetching) return
    setPage(previous => previous + 1)
  }, [isFetching])

  // Only the FIRST page's fetch should show the table's skeleton-row loading
  // state; a "Load more" fetch keeps the already-loaded rows on screen and
  // shows its own loading state on the button instead (see index.tsx).
  const isInitialLoading = isFetching && page === 1
  const isLoadingMore = isFetching && page > 1

  // Split by whether any row is already on screen: with none loaded, a
  // failure has nothing to preserve, so it replaces the table with
  // FeedbackTable's own full error state (isError below). With rows already
  // loaded, a failed "Load more" must NOT blank out what already rendered —
  // that failure surfaces next to the Load-more control instead
  // (loadMoreIsError, read by index.tsx), leaving the table and its rows
  // exactly as they were.
  const isError = queryIsError && rows.length === 0
  const loadMoreIsError = queryIsError && rows.length > 0

  return {
    rows,
    isInitialLoading,
    isLoadingMore,
    isError,
    loadMoreIsError,
    hasMore,
    onLoadMore,
    onRetry: refetch,
  }
}
